import { SimpleGrid } from "@mantine/core";
import { useEffect, useMemo, useRef } from "react";

import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useLogger } from "@/sdk";

import { ConfigMetricCard } from "../cards/ConfigMetricCard";
import { METRIC_KEYS } from "../data/metricKeys";
import { usePayMetrics } from "../data/usePayMetrics";
import { getMetricValue, isDataverseCard } from "../data/utils";
import { EMPTY_HUB_METRICS } from "../emptyHubMetrics";
import { TRANSFER_RATE_CARD_ID } from "../right-panel/MetricDetailPanel";
import { useHubPanelStore } from "../right-panel/useHubPanelStore";

// Dataverse keys whose calc arrives as a 0–1 fraction and must be scaled to a 0–100 percent before the
// card formats it (Transfer Rate is a rate; ERP already arrives as 0–100). This is intrinsic to the
// backend field, not a partner choice, so it lives here in the value layer — never in config, where a
// forgotten scale flag would silently render 0%.
const FRACTION_KEYS = new Set<string>([METRIC_KEYS.solve.TRANSFER_RATE]);

export function YesterdayMetrics() {
    const { payMetrics, isLoading, isError } = usePayMetrics();
    const togglePanel = useHubPanelStore((s) => s.togglePanel);
    const { hubMetrics } = usePartnerConfig();
    const logger = useLogger({ module: "YesterdayMetrics" });
    const hasCheckedMetrics = useRef(false);

    const dataverseCards = useMemo(() => (hubMetrics ?? EMPTY_HUB_METRICS).filter(isDataverseCard), [hubMetrics]);

    // A settled load where every configured card comes back empty usually means pay-metrics returned nothing
    // at all, not a genuinely quiet day — the two look identical on screen, so leave a one-time log breadcrumb
    // to tell them apart when investigating.
    useEffect(() => {
        const loadSettledWithCards = !isLoading && !isError && dataverseCards.length > 0;
        if (!loadSettledWithCards || hasCheckedMetrics.current) return;
        hasCheckedMetrics.current = true;

        const totals = payMetrics?.totals;
        const everyCardEmpty = dataverseCards.every(
            (card) => getMetricValue(totals?.[card.source.bucket], card.source.key) === null,
        );
        if (everyCardEmpty) {
            logger.debug(
                { cards: dataverseCards.map((card) => card.id) },
                "pay-metrics returned no values for any configured recent-stats card",
            );
        }
    }, [isLoading, isError, dataverseCards, payMetrics, logger]);

    // Only the transfer-rate detail panel needs these supporting fields (OCR / CRT); each card's own value
    // comes from its config entry's key. A card is clickable only when its panel's supporting data exists.
    const hasTransferRateSupporting = useMemo(() => {
        const totals = payMetrics?.totals;
        return (
            totals != null &&
            (totals.solve[METRIC_KEYS.solve.OCR] != null || totals.serve[METRIC_KEYS.serve.CRT] != null)
        );
    }, [payMetrics]);

    const panelOnClick: Record<string, () => void> = {};
    if (hasTransferRateSupporting) {
        panelOnClick[TRANSFER_RATE_CARD_ID] = () => togglePanel("transfer-rate");
    }

    const totals = payMetrics?.totals;

    return (
        <SimpleGrid type="container" cols={{ base: 1, "440px": 2, "680px": 3 }} spacing="md">
            {dataverseCards.map((card) => {
                const raw = getMetricValue(totals?.[card.source.bucket], card.source.key);
                const value = raw !== null && FRACTION_KEYS.has(card.source.key) ? raw * 100 : raw;
                return (
                    <ConfigMetricCard
                        key={card.id}
                        config={card}
                        displayValue={value}
                        isLoading={isLoading}
                        isError={isError}
                        isEmpty={value === null}
                        grade={card.grade}
                        onClick={panelOnClick[card.id]}
                    />
                );
            })}
        </SimpleGrid>
    );
}
